/**
 * Constantes compartilhadas entre subcomponentes de Preferencias.
 *
 * Spec docs/notificacoes/IMPLEMENTATION_SPEC_V1V2.md §3.4.
 */
import type {
  SubscriptionChannel,
  SubscriptionSeverity,
} from '@/api/notification-preferences'

/**
 * Sources visiveis na UI de preferencias: 14 V1 + 10 V2 (TASK-2594) + 1 SOC + host_order = 26.
 *
 * V2 sources chegam via /ingest/* endpoints (TASK-2582+).
 *
 * ESTA LISTA E O QUE EXISTE PARA O USUARIO. `webpush` esta em
 * `OPT_IN_ONLY_CHANNELS` (routing.py:81): sem linha em
 * `user_notification_subscriptions` o roteamento trata como opt-OUT e nao
 * entrega. Fonte fora daqui nao tem como ganhar essa linha pela interface —
 * entao a ausencia nao e "ninguem ligou", e impossibilidade.
 *
 * Guardado por `tests/unit/notifications/test_preference_sources_cobre_soc.py`,
 * que reprova se uma fonte do catalogo de SOC nao estiver aqui.
 */
export const PREFERENCE_SOURCES = [
  // V1 (14) — 'email' removido: era engano (email eh CHANNEL, nao source)
  'audit', 'notification', 'inbox', 'chamado',
  'cloudwatch_alarm', 'guardduty', 'securityhub',
  'zabbix', 'ztna', 'provisioning', 'database',
  'rbac', 'finops', 'gitlab',
  // V2 (10) — TASK-2594
  'cloudtrail', 'aws_health', 'aws_backup', 'cost_anomaly', 'alb_target_health',
  'ecs_service', 'gmud_lifecycle', 'provisioning_saga', 'cicd_gitlab', 'security_auth',
  // SOC (1) — TASK-10283. Quatro dos seis grupos do catalogo de SOC
  // (`soc_catalog.CATALOGO`) sao `wazuh:group:*`, e a fonte nunca esteve aqui:
  // em 01/09/2026 producao tinha 24 fontes com inscricao de webpush e `wazuh`
  // com ZERO pessoas.
  'wazuh',
  // Ordens no host (spec 2026-09-30 §6.4, TASK-12120): criar uma regra de ordem liga a
  // assinatura `host_order`/`robo` do dono. Sem a fonte aqui, essa linha existiria sem
  // como ser vista nem desligada pela interface.
  'host_order',
] as const

export type PreferenceSource = typeof PREFERENCE_SOURCES[number]

/** Canais master da pagina (in-app / email / push / whatsapp). */
export const PREFERENCE_CHANNELS: {
  key: SubscriptionChannel
  label: string
  shortLabel: string
}[] = [
  { key: 'feed', label: 'In-App (Feed)', shortLabel: 'In-app' },
  { key: 'inapp', label: 'Pop-up (tempo real)', shortLabel: 'Pop-up' },
  { key: 'email', label: 'E-mail', shortLabel: 'Email' },
  { key: 'webpush', label: 'Push (Web)', shortLabel: 'Push' },
  { key: 'whatsapp', label: 'WhatsApp', shortLabel: 'WhatsApp' },
]

// Master toggles globais (PreferenciasCanais): mantém in-app/email/push.
// 'inapp' (pop-up) e 'whatsapp' ficam só na matriz por-fonte (controle fino).
export const PREFERENCE_MASTER_CHANNELS = PREFERENCE_CHANNELS.filter(
  (c) => c.key !== 'whatsapp' && c.key !== 'inapp',
)

export const SEVERITY_OPTIONS: {
  value: SubscriptionSeverity
  label: string
}[] = [
  { value: 'debug', label: 'Debug' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
  { value: 'fatal', label: 'Fatal' },
]

/** Labels mais amigaveis pra sources (fallback = nome cru). */
export const SOURCE_LABELS: Record<string, string> = {
  // V1
  audit: 'Auditoria',
  notification: 'Notificação',
  inbox: 'Inbox',
  chamado: 'Chamados',
  cloudwatch_alarm: 'CloudWatch Alarm',
  guardduty: 'GuardDuty',
  securityhub: 'Security Hub',
  zabbix: 'Zabbix',
  ztna: 'ZTNA',
  provisioning: 'Provisioning',
  database: 'Database',
  rbac: 'RBAC',
  finops: 'FinOps',
  gitlab: 'GitLab',
  email: 'E-mail',
  // V2 (TASK-2594)
  cloudtrail: 'CloudTrail',
  aws_health: 'AWS Health',
  aws_backup: 'AWS Backup',
  cost_anomaly: 'Cost Anomaly',
  alb_target_health: 'ALB Target Health',
  ecs_service: 'ECS Service',
  gmud_lifecycle: 'GMUD Lifecycle',
  provisioning_saga: 'Provisioning Saga',
  cicd_gitlab: 'CI/CD GitLab',
  security_auth: 'Security Auth',
  // SOC (TASK-10283)
  wazuh: 'Wazuh (SIEM)',
  // Ordens no host (TASK-12120)
  host_order: 'Ordens no host',
  // Vigia de postura/operacao do @DevOpsSRE (MIGRATION_405)
  devops_sre: 'DevOps SRE',
}

export function sourceLabel(src: string): string {
  return SOURCE_LABELS[src] ?? src
}
